import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { requireGlobalCapability } from '@/lib/route-guards';
import { searchEnum } from '@/lib/search-enum';
import { warm } from '@/api/loaders';
import { adminProviderDrift } from '@/api/resources';
import {
  DRIFT_SCREEN_DEFAULT_WINDOW,
  DRIFT_WINDOWS,
  providerDriftQuery,
} from '@/lib/provider-drift';
import type { DriftWindow } from '@/lib/provider-drift';
import { ProviderDriftView } from '@/screens/admin-provider-drift';

/**
 * How far back to look, in the URL.
 *
 * Both of this route's search params GATE THE READ — they are the cache key —
 * so neither may be a `useState`: a gate that only exists once the component
 * has mounted is unreachable from the loader that runs before it, and every
 * switch becomes a cold fetch onto an empty body. In the URL they also make an
 * incident linkable, which for a screen whose whole job is explaining one
 * campaign's overwritten settings is most of the point.
 */
const driftWindow = searchEnum('window', DRIFT_WINDOWS, DRIFT_SCREEN_DEFAULT_WINDOW);

export const Route = createFileRoute('/admin/provider-drift')({
  // Matches `require_drift_access` in the API: Full `config_manage` AND an
  // unscoped admin. `requireCapability` alone would land a production-scoped
  // admin on a screen that can only ever 403 — drift spans every production and
  // the per-entity scope checks that would make a scoped read safe do not exist.
  beforeLoad: requireGlobalCapability('config_manage', 'full'),
  validateSearch: (search: Record<string, unknown>) => ({
    window: driftWindow.parse(search),
    view: search.view === 'history' ? 'history' as const : 'unresolved' as const,
    target: search.target === 'meta' || search.target === 'ttd' ? search.target : undefined,
    lifecycle: ['current', 'retired', 'safety'].includes(String(search.lifecycle)) ? String(search.lifecycle) : undefined,
    status: typeof search.status === 'string' && search.status ? search.status : undefined,
    cursor: typeof search.cursor === 'string' && search.cursor ? search.cursor : undefined,
    // A campaign id is an open set, not a closed one, so it is carried as-is
    // and answered by the server. Empty collapses to absent so `?campaign=`
    // cannot open a second cache slot meaning "no filter".
    campaign:
      typeof search.campaign === 'string' && search.campaign ? search.campaign : undefined,
  }),
  loaderDeps: ({ search }) => search,
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  // One read, and the screen is nothing without it, so this blocks rather than
  // warming in the background. `providerDriftQuery` is the same function the
  // screen calls — the resource declares no `keyOf`, so the query object IS the
  // key and a literal here would fill a slot nothing subscribes to.
  loader: ({ context, deps }) =>
    warm(context.queryClient, adminProviderDrift(providerDriftQuery(deps.window, deps.campaign, { view: deps.view, target: deps.target, lifecycle: deps.lifecycle, status: deps.status, cursor: deps.cursor }))),
  component: ProviderDriftRoute,
});

function ProviderDriftRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <ProviderDriftView
      filters={{ view: search.view, target: search.target, lifecycle: search.lifecycle, status: search.status, cursor: search.cursor }}
      onFiltersChange={(next) => void navigate({ search: (prev) => ({ ...prev, cursor: undefined, ...next }) })}
      driftWindow={search.window}
      campaignId={search.campaign}
      onWindowChange={(next: DriftWindow) =>
        void navigate({ search: (prev) => ({ ...prev, window: next, cursor: undefined }) })
      }
      onCampaignChange={(next: string | undefined) =>
        void navigate({ search: (prev) => ({ ...prev, campaign: next, cursor: undefined }), replace: true })
      }
    />
  );
}
